/**
 * Требования готовности этапа.
 *
 * Требование — измеримое условие, а не задача: оно либо выполнено, либо нет.
 * К требованию привязываются результаты — элементы проекта, которые его
 * закрывают. Связь many-to-many: одно требование могут закрывать несколько
 * результатов, и один результат может закрывать несколько требований.
 */

import { ChevronDownIcon, Loader2Icon, LinkIcon, PlusIcon, TrashIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  REQUIREMENT_STATUSES,
  REQUIREMENT_STATUS_LABELS_RU,
  type ProjectStageDto,
  type RequirementStatus,
  type StageRequirementDto,
} from '@project-os/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ItemListResponse, ItemRefDto } from '@project-os/contracts';
import { apiRequest, ApiError } from '@/api/client';
import { useProjectMembers } from '@/api/members';
import {
  useCreateRequirement,
  useLinkRequirementItem,
  useRemoveRequirement,
  useUnlinkRequirementItem,
  useUpdateRequirement,
} from '@/api/stages';
import { StatusBadge } from '@/components/common/status-badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ItemTypeIcon } from '../items/item-icon';

const NONE = '__none__';

const STATUS_TONES: Record<RequirementStatus, 'neutral' | 'progress' | 'success' | 'danger'> = {
  TODO: 'neutral',
  IN_PROGRESS: 'progress',
  BLOCKED: 'danger',
  DONE: 'success',
  SKIPPED: 'neutral',
};

export function RequirementList({
  projectId,
  stage,
  canEdit,
  defaultOpen,
}: {
  projectId: string;
  stage: ProjectStageDto;
  canEdit: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [newTitle, setNewTitle] = useState('');
  const create = useCreateRequirement(projectId);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    try {
      await create.mutateAsync({ stageId: stage.id, title, status: 'TODO' });
      setNewTitle('');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось добавить требование');
    }
  };

  if (stage.requirements.length === 0 && !canEdit) {
    return <p className="text-xs text-muted-foreground">Требования не заданы</p>;
  }

  return (
    <div className="space-y-3">
      {stage.requirements.length > 0 ? (
        <>
          {!open ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => setOpen(true)}
            >
              <ChevronDownIcon className="size-3.5" />
              Показать требования ({stage.requirements.length})
            </Button>
          ) : (
            <ul className="divide-y rounded-md border">
              {stage.requirements.map((requirement) => (
                <RequirementRow
                  key={requirement.id}
                  projectId={projectId}
                  requirement={requirement}
                  canEdit={canEdit}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}

      {canEdit && (open || stage.requirements.length === 0) ? (
        <div className="flex gap-2">
          <Input
            value={newTitle}
            onChange={(event) => setNewTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleCreate();
              }
            }}
            placeholder="Новое требование: что должно быть выполнено"
            className="h-8"
            maxLength={300}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={!newTitle.trim() || create.isPending}
            onClick={() => void handleCreate()}
          >
            {create.isPending ? <Loader2Icon className="animate-spin" /> : <PlusIcon />}
            Добавить
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RequirementRow({
  projectId,
  requirement,
  canEdit,
}: {
  projectId: string;
  requirement: StageRequirementDto;
  canEdit: boolean;
}) {
  const { data: members = [] } = useProjectMembers(projectId);
  const update = useUpdateRequirement(projectId);
  const remove = useRemoveRequirement(projectId);
  const unlink = useUnlinkRequirementItem(projectId);

  const busy = update.isPending || remove.isPending || unlink.isPending;

  const change = async (patch: { status?: RequirementStatus; ownerId?: string | null }) => {
    try {
      await update.mutateAsync({ requirementId: requirement.id, ...patch });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить требование');
    }
  };

  return (
    <li className="px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex min-w-56 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm">{requirement.title}</span>

          {requirement.linkedItems.map((item) => (
            <span
              key={item.id}
              className="inline-flex max-w-64 items-center gap-1.5 rounded-md border bg-muted/60 px-1.5 py-0.5 text-2xs"
            >
              <ItemTypeIcon type={item.type} className="size-3 shrink-0" />
              <span className="truncate">{item.title}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={busy}
                  onClick={() =>
                    void unlink.mutateAsync({ requirementId: requirement.id, itemId: item.id })
                  }
                  aria-label={`Отвязать «${item.title}»`}
                >
                  <XIcon className="size-3" />
                </button>
              ) : null}
            </span>
          ))}
        </span>

        {canEdit ? (
          <>
            <Select
              value={requirement.status}
              disabled={busy}
              onValueChange={(value) => void change({ status: value as RequirementStatus })}
            >
              <SelectTrigger size="sm" className="w-40" aria-label="Состояние требования">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REQUIREMENT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {REQUIREMENT_STATUS_LABELS_RU[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={requirement.owner?.id ?? NONE}
              disabled={busy}
              onValueChange={(value) => void change({ ownerId: value === NONE ? null : value })}
            >
              <SelectTrigger size="sm" className="w-44" aria-label="Ответственный за требование">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Без ответственного</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.user.id} value={member.user.id}>
                    {member.user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <LinkItemButton projectId={projectId} requirementId={requirement.id} />

            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={() => void remove.mutateAsync(requirement.id)}
              aria-label="Удалить требование"
            >
              <TrashIcon className="size-3.5" />
            </Button>
          </>
        ) : (
          <>
            <StatusBadge
              label={REQUIREMENT_STATUS_LABELS_RU[requirement.status]}
              tone={STATUS_TONES[requirement.status]}
            />
            <span className="w-44 truncate text-xs text-muted-foreground">
              {requirement.owner?.name ?? 'Без ответственного'}
            </span>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * Привязка результата к требованию.
 *
 * Ищет по элементам текущего проекта тем же запросом, что и страница
 * «Элементы»: отдельного эндпоинта для этого не нужно.
 */
function LinkItemButton({
  projectId,
  requirementId,
}: {
  projectId: string;
  requirementId: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const link = useLinkRequirementItem(projectId);
  const options = useItemsForLinking(projectId, debounced, open);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = async (itemId: string) => {
    try {
      await link.mutateAsync({ requirementId, itemId });
      toast.success('Результат привязан к требованию');
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось привязать результат');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground"
          aria-label="Привязать результат"
          title="Привязать результат, который закрывает требование"
        >
          {link.isPending ? (
            <Loader2Icon className="size-3.5 animate-spin" />
          ) : (
            <LinkIcon className="size-3.5" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Поиск элемента проекта…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {options.isPending ? (
              <div className="p-3 text-xs text-muted-foreground">Загрузка…</div>
            ) : (
              <>
                <CommandEmpty>Ничего не найдено</CommandEmpty>
                <CommandGroup>
                  {(options.data ?? []).map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => void handleSelect(item.id)}
                      className="gap-2"
                    >
                      <ItemTypeIcon type={item.type} className="size-3.5" />
                      <span className="min-w-0 flex-1 truncate">{item.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Элементы проекта для привязки к требованию. */
function useItemsForLinking(projectId: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: ['projects', projectId, 'items', 'for-linking', search],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ limit: '20' });
      if (search) params.set('search', search);
      const response = await apiRequest<ItemListResponse>(
        `/projects/${projectId}/items?${params.toString()}`,
        { signal },
      );
      return response.data.map((item): ItemRefDto => ({
        id: item.id,
        type: item.type,
        title: item.title,
        status: item.status,
      }));
    },
    enabled,
  });
}
